import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Optimistically seed a polled-status cache entry right after its trigger
 * mutation succeeds, WITHOUT invalidating the query.
 *
 * Why not invalidate: an immediate refetch can race the backend's status write
 * and return a not-started/empty status, clobbering this seed. Both the
 * split-status and sample-enrichment auto-trigger guards are keyed off the
 * seeded status/workflowId; a clobbered seed would let the guard re-fire the
 * run, and each re-fire clean-slates progress back to 0. The query is already
 * polling, so it picks up real progress on its next tick — the seed only has to
 * bridge the gap until then (and survive a remount before the first poll lands).
 *
 * `next` receives the previous cache value so callers can preserve fields the
 * trigger response doesn't carry (e.g. `startedAt`, prior counters).
 */
export function seedPolledStatus<T>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  next: (prev: T | undefined) => T,
): void {
  queryClient.setQueryData<T>(queryKey, next);
}
